import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import {
  Plus,
  Upload,
  Loader2,
  X,
  ChevronDown,
  ChevronUp,
  Download,
  CalendarClock,
  Check,
  Tag,
  CreditCard,
  Banknote,
  Smartphone,
  Building2,
  FileText,
  LinkIcon,
  GraduationCap,
  Send,
  TicketPercent,
} from "lucide-react";
import { useRazorpayStatus } from "@/features/razorpay/api/useRazorpayMutations";
import CollectPaymentDialog from "@/features/razorpay/components/CollectPaymentDialog";
import PaymentLinksList from "@/features/razorpay/components/PaymentLinksList";
import { Button } from "@/components/ui/button";
import PaymentHistoryTimeline from "./PaymentHistoryTimeline";
import InstallmentSchedule from "../installments/InstallmentSchedule";
import InstallmentPlanModal from "../installments/InstallmentPlanModal";
import ForecloseDialog from "../installments/ForecloseDialog";
import InstallmentPicker from "../installments/InstallmentPicker";
import { previewAllocation, outstandingOf } from "../installments/installmentMath";
import { SingleDatePicker } from "@/components/ui/library/single-date-picker";
import TimeOfDayField from "@/features/leads/components/leadFilter/components/DateFilters/TimeOfDayField";
import BankNameInput from "./BankNameInput";
import AttachServiceModal from "./AttachServiceModal";
import AttachCourseModal from "./AttachCourseModal";
import type { DealPayment } from "../mockData";
import {
  DEAL_EDIT_DENIED_MESSAGE,
  useCanEditDeal,
} from "@/rbac/useCanEditDeal";
import { useCreatePayment } from "@/features/leads/api/usePaymentMutations";
import {
  useSettleInstallments,
  useApplyInstallmentCredit,
  useReopenInstallmentPlan,
  useRebuildInstallmentPlan,
} from "@/features/leads/api/useInstallmentMutations";
import { useDocumentMutaion } from "@/hooks/api/useDocmentMutation";
import { useAppSelector } from "@/stores/hook";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import useCurrentIndustry from "@/hooks/globalapis/useCurrentIndustry";
import { generateDealInvoicePdf } from "@/module/videoProduction/helpers/generateDocumentPdf";
import { generateAcademyDealInvoicePdf } from "@/module/academy/helpers/generateAcademyDocumentPdf";
import DealCouponPanel from "@/features/coupons/components/DealCouponPanel";
import { useRemoveCoupon } from "@/features/coupons/api/useCoupons";
import { useCouponPermissions } from "@/rbac/useCouponPermissions";
import ConfirmDialog from "@/components/ui/ConfirmDialog";

function fmt(n: number) {
  return n.toLocaleString("en-IN");
}

/**
 * The form keeps `paidAt` as the "YYYY-MM-DDTHH:mm" string it always used, so
 * `handleSave` and `resetForm` are untouched. These two just split and rejoin it
 * for the date and time controls.
 */
function splitDateTime(v: string): { date: string; time: string } {
  const [d = "", t = ""] = (v || "").split("T");
  return { date: d, time: t.slice(0, 5) };
}
function joinDateTime(date: string, time: string): string {
  return `${date || ""}T${time || "00:00"}`;
}

function nowDatetimeLocal() {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const STATUS_BADGE: Record<
  string,
  { label: string; bg: string; text: string }
> = {
  advance: { label: "Advance", bg: "#e0f2fe", text: "#0369a1" },
  partial: { label: "Partial", bg: "#fef3c7", text: "#92400e" },
  full: { label: "Paid", bg: "#dcfce7", text: "#166534" },
  pending: { label: "Pending", bg: "#fee2e2", text: "#991b1b" },
};

const PAYMENT_METHODS = [
  { value: "UPI", label: "UPI", icon: Smartphone },
  { value: "Bank Transfer", label: "Bank Transfer", icon: Building2 },
  { value: "Cash", label: "Cash", icon: Banknote },
  { value: "Card", label: "Card", icon: CreditCard },
  { value: "Cheque", label: "Cheque", icon: FileText },
] as const;

function PaymentMethodDropdown({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isDiscount = value === "Discount";

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const activeMethod = PAYMENT_METHODS.find((m) => m.value === value);
  const ActiveIcon = activeMethod?.icon;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`h-9 w-full px-3 text-[13px] rounded-[8px] border bg-white flex items-center justify-between gap-2 transition-all ${
          isDiscount
            ? "border-[#8b5cf6]/40 text-[#7c3aed]"
            : open
              ? "border-[#46a580] ring-2 ring-[#46a580]/10"
              : "border-[#e2e4e3] hover:border-[#c5c9c7]"
        }`}
      >
        <span className="flex items-center gap-2 truncate">
          {isDiscount ? (
            <>
              <Tag className="size-3.5 text-[#8b5cf6]" />
              <span className="font-medium">Discount</span>
            </>
          ) : (
            <>
              {ActiveIcon && <ActiveIcon className="size-3.5 text-[#697571]" />}
              <span className="font-medium">{value}</span>
            </>
          )}
        </span>
        <ChevronDown
          className={`size-3.5 text-[#9ba3a0] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-[#e2e4e3] rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,0.12)] py-1 overflow-hidden">
          {PAYMENT_METHODS.map((m) => {
            const Icon = m.icon;
            const selected = value === m.value;
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => {
                  onChange(m.value);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-left transition-colors ${
                  selected
                    ? "bg-[#f0faf5] text-[#267355] font-semibold"
                    : "text-[#374741] hover:bg-[#f7f9f8]"
                }`}
              >
                <Icon
                  className={`size-4 ${selected ? "text-[#267355]" : "text-[#9ba3a0]"}`}
                />
                {m.label}
              </button>
            );
          })}

          <div className="mx-3 my-1 border-t border-dashed border-[#d4d6d5]" />

          <button
            type="button"
            onClick={() => {
              onChange("Discount");
              setOpen(false);
            }}
            className={`w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-left transition-colors ${
              isDiscount
                ? "bg-[#f5f3ff] text-[#7c3aed] font-semibold"
                : "text-[#697571] hover:bg-[#faf8ff] hover:text-[#7c3aed]"
            }`}
          >
            <Tag
              className={`size-4 ${isDiscount ? "text-[#7c3aed]" : "text-[#a78bfa]"}`}
            />
            <div className="flex flex-col">
              <span>Discount</span>
              <span className="text-[10px] text-[#9ba3a0] font-normal leading-tight">
                Write off pending amount
              </span>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

export default function DealPaymentCard({
  deal,
  isAddOpen,
  onToggleAdd,
  isHistoryOpen,
  onToggleHistory,
  installmentIdToOpen = null,
}: {
  deal: DealPayment;
  isAddOpen: boolean;
  onToggleAdd: () => void;
  isHistoryOpen: boolean;
  onToggleHistory: () => void;
  /**
   * Instalment the Payments tab was deep-linked to — set when the operator hits
   * "Record payment" on the instalment-due popup. Preselects the row so the
   * money lands where the reminder said it should, rather than on whatever the
   * form happened to default to.
   */
  installmentIdToOpen?: number | null;
}) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("UPI");
  const [bankName, setBankName] = useState("");
  const [paidAt, setPaidAt] = useState(nowDatetimeLocal());
  const [note, setNote] = useState("");
  const [receiptDocId, setReceiptDocId] = useState<number | null>(null);
  const [receiptFileName, setReceiptFileName] = useState("");
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const createPayment = useCreatePayment();
  const settleInstallments = useSettleInstallments();
  const applyCredit = useApplyInstallmentCredit();
  const reopenPlan = useReopenInstallmentPlan();
  const rebuildPlan = useRebuildInstallmentPlan();
  /**
   * Discount → "Amount" (the write-off below, unchanged) or "Coupon code"
   * (server-computed discount on the deal's value). The coupon choice only
   * appears for roles allowed to apply coupons.
   */
  const { canApply: canApplyCoupon } = useCouponPermissions();
  const [discountMode, setDiscountMode] = useState<"amount" | "coupon">("amount");
  const [confirmRemoveCoupon, setConfirmRemoveCoupon] = useState(false);
  const removeCoupon = useRemoveCoupon();
  const { uploadFile } = useDocumentMutaion();
  const {
    currentUser: {
      user: { tenant },
    },
  } = useAppSelector((s) => s.auth);
  const { lead } = useActiveDataMutation();
  const { industry } = useCurrentIndustry();

  /** Which instalment the payment form is being recorded against. */
  const [focusInstallmentId, setFocusInstallmentId] = useState<number | null>(
    installmentIdToOpen,
  );
  /**
   * "Add money" mode: record cash that is NOT for a particular instalment. It
   * lands as unadjusted credit on the deal, which the schedule then offers to
   * apply. Without this there was no way at all to record such a payment once a
   * plan existed — the picker is mandatory otherwise.
   */
  const [unadjustedMode, setUnadjustedMode] = useState(false);

  /**
   * The form opens BELOW the schedule, which on a six-row plan is well past the
   * bottom of the drawer — so pressing "+" appeared to do nothing at all. Bring
   * it into view when it opens.
   *
   * `block: "nearest"` rather than "center": if the form is already visible this
   * does nothing, so simply having it open never yanks the page while someone is
   * reading the ladder above it.
   */
  const formRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isAddOpen) return;
    const el = formRef.current;
    if (!el) return;
    const id = window.setTimeout(
      () => el.scrollIntoView({ behavior: "smooth", block: "nearest" }),
      60, // let the form paint before measuring it
    );
    return () => window.clearTimeout(id);
  }, [isAddOpen]);

  // Follow the deep link when it changes (the popup can fire again while the
  // drawer is already open, pointing at a different instalment).
  useEffect(() => {
    if (installmentIdToOpen != null) setFocusInstallmentId(installmentIdToOpen);
  }, [installmentIdToOpen]);

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showForeclose, setShowForeclose] = useState(false);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [isRzpAddOpen, setIsRzpAddOpen] = useState(false);
  const hasNoVariant = deal.totalValue === 0 && deal.netAmount === 0;
  // Attaching a course / service variant edits the deal — "Edit choices" on
  // its own stage. Payments, plans and receipts are NOT gated by this.
  const canEditDeal = useCanEditDeal();
  const canAttachVariant = canEditDeal(deal.stageId);

  const { data: rzpStatusRes } = useRazorpayStatus();
  // Who a payment link goes to — the WhatsApp/email senders read this.
  const paymentLinkLead = lead
    ? {
        id: Number(lead.id) || undefined,
        name: lead.name ?? null,
        phone: lead.phone ?? null,
        phoneCountryCode: lead.phoneCountryCode ?? null,
        email: lead.email ?? null,
      }
    : null;
  const rzpConnected =
    !!rzpStatusRes?.data?.connected && !!rzpStatusRes?.data?.isActive;

  const { date: paidAtDate, time: paidAtTime } = splitDateTime(paidAt);
  const isDiscount = method === "Discount";
  const isCouponMode = isDiscount && canApplyCoupon && discountMode === "coupon";
  const parsedAmount = parseFloat(amount) || 0;
  const isAmountValid = parsedAmount > 0 && parsedAmount <= deal.pendingAmount;

  /**
   * Instalments this payment could settle, earliest first. Paid and waived rows
   * are gone — you cannot pay something twice.
   */
  const unpaidInstallments = useMemo(() => {
    const plan = deal.installmentPlan;
    if (!plan) return [];
    return [...plan.installments]
      .filter((i) => i.status !== "PAID" && i.status !== "WAIVED")
      .sort((a, b) => a.seq - b.seq)
      .map((i) => ({
        id: i.id,
        seq: i.seq,
        dueDate: i.dueDate,
        outstanding: outstandingOf(i),
      }));
  }, [deal.installmentPlan]);

  /**
   * A deal WITH a plan must attribute its money — that is the whole point of
   * having a schedule, and the product decision was explicit: the user picks,
   * there is no silent waterfall.
   *
   * `Discount` is the deliberate exception. Writing an amount off is not an
   * instalment payment, so it stays unattributed rather than pretending a row
   * was collected.
   */
  const requiresInstallment =
    !!deal.installmentPlan &&
    unpaidInstallments.length > 0 &&
    !isDiscount &&
    !unadjustedMode;

  const selectedInstallment =
    unpaidInstallments.find((i) => i.id === focusInstallmentId) ?? null;

  /** What the entered amount would actually settle, shown before saving. */
  const allocation = useMemo(() => {
    if (!requiresInstallment || !selectedInstallment || parsedAmount <= 0) {
      return null;
    }
    return previewAllocation(
      parsedAmount,
      selectedInstallment.id,
      unpaidInstallments,
    );
  }, [requiresInstallment, selectedInstallment, parsedAmount, unpaidInstallments]);

  /**
   * WHY the Save button is dead, in words.
   *
   * The commonest case here is not user error at all: the plan was built for
   * more than the deal is worth, so an instalment can legitimately be larger
   * than the deal's remaining balance and the payment is refused by a cap the
   * operator never set. A greyed-out button teaches them nothing; naming the
   * number and the fix does.
   */
  const blockedReason = useMemo(() => {
    if (!amount || parsedAmount <= 0) return null; // nothing typed yet — not an error
    if (parsedAmount > deal.pendingAmount) {
      const over = deal.installmentPlan
        ? " The schedule is bigger than the deal — change the plan to the deal's value."
        : "";
      return `${fmt(parsedAmount)} is more than the deal's remaining ${fmt(deal.pendingAmount)}.${over}`;
    }
    if (requiresInstallment && !selectedInstallment) {
      return "Pick which instalment this is for.";
    }
    return null;
  }, [
    amount,
    parsedAmount,
    deal.pendingAmount,
    deal.installmentPlan,
    requiresInstallment,
    selectedInstallment,
  ]);

  const canSave =
    isAmountValid &&
    !createPayment.isPending &&
    !settleInstallments.isPending &&
    (!requiresInstallment || !!selectedInstallment);

  const badge = STATUS_BADGE[deal.status] || STATUS_BADGE.pending;
  const paidPercent =
    deal.netAmount > 0 ? (deal.totalPaid / deal.netAmount) * 100 : 0;

  const usedBankNames = useMemo(
    () => deal.payments.map((p) => p.txnId).filter(Boolean) as string[],
    [deal.payments]
  );

  const handleDownloadInvoice = useCallback(() => {
    let addressStr = "";
    let gstinStr = "";
    let paymentInfoStr = "";
    try {
      const parsed = JSON.parse(tenant?.tenantAddress || "{}");
      addressStr = parsed.address || "";
      gstinStr = parsed.gstin || "";
      paymentInfoStr = parsed.paymentInfo || "";
    } catch {
      addressStr = tenant?.tenantAddress || "";
    }
    const tenantInfo = {
      tenantName: tenant?.tenantName || "",
      tenantLogoUrl: tenant?.tenantLogoUrl || "",
      tenantAddress: addressStr,
      gstin: gstinStr,
      paymentInfo: paymentInfoStr,
    };
    const clientName = lead?.name || lead?.phone || "Client";
    if (industry?.id === 5) {
      generateAcademyDealInvoicePdf({ deal, tenant: tenantInfo, clientName });
    } else {
      generateDealInvoicePdf({ deal, tenant: tenantInfo, clientName });
    }
  }, [deal, tenant, lead, industry]);

  const resetForm = () => {
    setAmount("");
    setMethod("UPI");
    setBankName("");
    setPaidAt(nowDatetimeLocal());
    setNote("");
    setReceiptDocId(null);
    setReceiptFileName("");
    setDiscountMode("amount");
  };

  const handleSave = () => {
    if (!canSave) return;

    // With a plan, send the EXACT split the operator was just shown. The server
    // writes one payment row per instalment in a single transaction, so a
    // ₹100,000 transfer across #7, #8 and part of #9 stays one receipt.
    if (
      requiresInstallment &&
      allocation &&
      (allocation.slices.length > 0 || allocation.unallocated > 0)
    ) {
      settleInstallments.mutate(
        {
          childId: Number(deal.dealId),
          method,
          paidAt: paidAt ? new Date(paidAt).toISOString() : undefined,
          txnId: bankName || undefined,
          note: note || undefined,
          receiptDocId: receiptDocId ?? undefined,
          allocations: allocation.slices.map((a) => ({
            installmentId: a.installmentId,
            amount: a.amount,
          })),
          // Whatever the ladder cannot absorb is still the customer's money —
          // it is stored against the deal as unadjusted, never trimmed away.
          unadjustedAmount: allocation.unallocated,
        },
        {
          onSuccess: () => {
            resetForm();
            setFocusInstallmentId(null);
            setUnadjustedMode(false);
            onToggleAdd();
          },
        },
      );
      return;
    }

    createPayment.mutate(
      {
        childId: Number(deal.dealId),
        amount: parsedAmount,
        method,
        paidAt: paidAt ? new Date(paidAt).toISOString() : undefined,
        txnId: bankName || undefined,
        note: note || undefined,
        receiptDocId: receiptDocId ?? undefined,
      },
      {
        onSuccess: () => {
          resetForm();
          onToggleAdd();
        },
      }
    );
  };

  const handleReceiptUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingReceipt(true);
    setReceiptFileName(file.name);
    uploadFile.mutate(
      {
        file,
        attachedTo: "child",
        attachedScope: deal.dealId,
        category: "payment",
        name: file.name,
        attachmentLabel: "PAYMENT_RECEIPT",
      },
      {
        onSuccess: (data: any) => {
          const docId = data?.data?.id ?? data?.document?.id ?? data?.id;
          if (docId) setReceiptDocId(docId);
          setUploadingReceipt(false);
        },
        onError: () => {
          setReceiptFileName("");
          setUploadingReceipt(false);
        },
      }
    );
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="rounded-[14px] border border-[#e6e8e7] bg-white overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
      {/* Header row */}
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          onClick={onToggleHistory}
          className="flex-1 flex items-center gap-3 min-w-0 text-left hover:bg-[#fafbfa] -ml-2 pl-2 -my-1 py-1 rounded-lg transition-colors"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2.5">
              <span className="text-[14px] font-semibold text-[#1e302a] truncate">
                {deal.dealName}
              </span>
              <span className="text-[11px] text-[#8a918d] bg-[#f3f4f3] px-2 py-0.5 rounded-[5px] shrink-0 font-medium">
                {deal.stageName}
              </span>
            </div>
            {(deal.universityName || deal.courseName) && (
              <div className="flex items-center gap-1.5 mt-0.5">
                {deal.universityName && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-[#697571]">
                    <Building2 className="size-2.5 shrink-0 text-[#9ba3a0]" />
                    <span className="truncate max-w-[120px]">
                      {deal.universityName}
                    </span>
                  </span>
                )}
                {deal.universityName && deal.courseName && (
                  <span className="text-[#c5c9c7] text-[10px]">·</span>
                )}
                {deal.courseName && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-[#697571]">
                    <GraduationCap className="size-2.5 shrink-0 text-[#9ba3a0]" />
                    <span className="truncate max-w-[120px]">
                      {deal.courseName}
                    </span>
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2.5 shrink-0">
            <span className="text-[14px] font-bold text-[#1e302a] tabular-nums">
              {fmt(deal.totalPaid)}
              <span className="text-[12px] text-[#9ba3a0] font-normal">
                /{fmt(deal.netAmount)}
              </span>
            </span>
            <span
              className="text-[10px] font-bold px-2 py-[2px] rounded-full"
              style={{ backgroundColor: badge.bg, color: badge.text }}
            >
              {badge.label}
            </span>
            {isHistoryOpen ? (
              <ChevronUp className="size-4 text-[#9ba3a0]" />
            ) : (
              <ChevronDown className="size-4 text-[#9ba3a0]" />
            )}
          </div>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDownloadInvoice();
          }}
          title="Download deal invoice"
          className="size-8 flex items-center justify-center rounded-[8px] border border-[#e2e4e3] text-[#697571] hover:bg-[#f7f9f8] hover:text-[#374741] transition-all shrink-0"
        >
          <Download className="size-3.5" />
        </button>

        {/* EMI plan. Hidden while no variant is attached — there is no priced
            principal to amortise, and the purple "attach one to enable
            payments" hint below already says why. Frozen deals are paused
            alternatives, so no schedule for those either. */}
        {!hasNoVariant && !deal.isFroze && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowPlanModal(true);
            }}
            title={
              deal.installmentPlan
                ? "Edit instalment plan"
                : "Set up instalment plan"
            }
            className={`size-8 flex items-center justify-center rounded-[8px] transition-all shrink-0 ${
              deal.installmentPlan
                ? "border border-[#e2e4e3] text-[#697571] hover:bg-[#f7f9f8] hover:text-[#374741]"
                : "border-2 border-[#c1e1d5] text-[#2d7a5a] hover:bg-[#f0faf5] hover:border-[#267355]"
            }`}
          >
            <CalendarClock className="size-3.5" strokeWidth={2.2} />
          </button>
        )}

        {hasNoVariant ? (
          canAttachVariant && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAttachModal(true);
              }}
              className="size-8 flex items-center justify-center rounded-[8px] border-2 border-[#e0c6ff] text-[#7c3aed] hover:bg-[#faf8ff] hover:border-[#7c3aed] transition-all shrink-0"
              title={
                industry?.id === 1
                  ? "Attach a course"
                  : "Attach a service variant"
              }
            >
              <LinkIcon className="size-4" strokeWidth={2.5} />
            </button>
          )
        ) : deal.pendingAmount > 0 ? (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isRzpAddOpen) setIsRzpAddOpen(false);
                onToggleAdd();
              }}
              className={`size-8 flex items-center justify-center rounded-[8px] border-2 transition-all shrink-0 ${
                isAddOpen
                  ? "bg-[#267355] border-[#267355] text-white shadow-sm"
                  : "border-[#c1e1d5] text-[#2d7a5a] hover:bg-[#f0faf5] hover:border-[#267355]"
              }`}
              title="Record payment"
            >
              <Plus className="size-4" strokeWidth={2.5} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!rzpConnected) return;
                if (isAddOpen) onToggleAdd();
                setIsRzpAddOpen(true);
              }}
              disabled={!rzpConnected}
              title={
                rzpConnected
                  ? "Collect payment — Razorpay link, recorded when paid"
                  : "Connect Razorpay in Apps to enable"
              }
              className={`size-8 flex items-center justify-center rounded-[8px] border-2 transition-all shrink-0 ${
                !rzpConnected
                  ? "border-[#e2e4e3] text-[#c5c9c7] cursor-not-allowed"
                  : isRzpAddOpen
                    ? "bg-[#3395FF] border-[#3395FF] text-white shadow-sm"
                    : "border-[#bcdcff] text-[#1f6fd1] hover:bg-[#eef5ff] hover:border-[#3395FF]"
              }`}
            >
              <Send className="size-4" strokeWidth={2.5} />
            </button>
          </>
        ) : null}
      </div>

      {/* Progress bar */}
      <div className="px-4 pb-2">
        <div className="h-[4px] rounded-full bg-[#e8eae9] overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${Math.min(paidPercent, 100)}%`,
              background:
                paidPercent >= 100
                  ? "linear-gradient(90deg, #46a580, #2d7a5a)"
                  : "linear-gradient(90deg, #f59e0b, #d97706)",
            }}
          />
        </div>
      </div>

      {/* The coupon this deal's discount carries — code, amount, and (for roles
          that may apply coupons) Remove, which puts the deal's money back. */}
      {deal.coupon && (
        <div className="mx-4 mb-2 flex items-center justify-between gap-2 rounded-lg border border-[#e0c6ff] bg-[#faf8ff] px-2.5 py-1.5">
          <span className="flex items-center gap-1.5 text-[11px] text-[#6d28d9] min-w-0">
            <TicketPercent className="size-3.5 shrink-0" />
            <span className="truncate">
              Coupon <span className="font-semibold tracking-[0.03em]">{deal.coupon.code}</span>
              {deal.discountAmount > 0 && (
                <span className="tabular-nums"> · −₹{fmt(deal.discountAmount)}</span>
              )}
            </span>
          </span>
          {canApplyCoupon && (
            <button
              type="button"
              onClick={() => setConfirmRemoveCoupon(true)}
              disabled={removeCoupon.isPending}
              className="shrink-0 text-[10px] font-medium text-[#7c3aed] underline hover:text-[#5b21b6] disabled:opacity-50"
            >
              Remove
            </button>
          )}
        </div>
      )}

      {/* EMI schedule — only when the deal has a plan. Sits between the money
          progress bar and the payment form so the ladder reads as part of the
          deal's money, not as a separate feature bolted underneath. */}
      {deal.installmentPlan && (
        <InstallmentSchedule
          plan={deal.installmentPlan}
          dealTotalPaid={deal.totalPaid}
          dealNetAmount={deal.netAmount}
          onAddUnadjusted={() => {
            setUnadjustedMode(true);
            setFocusInstallmentId(null);
            setAmount("");
            if (!isAddOpen) onToggleAdd();
          }}
          onApplyCredit={() => applyCredit.mutate(Number(deal.dealId))}
          applyingCredit={applyCredit.isPending}
          onForeclose={() => setShowForeclose(true)}
          onRebuild={() => setShowPlanModal(true)}
          onReopen={() =>
            deal.installmentPlan && reopenPlan.mutate(deal.installmentPlan.id)
          }
          reopening={reopenPlan.isPending}
          onCloseRemaining={() =>
            deal.installmentPlan &&
            rebuildPlan.mutate({
              planId: deal.installmentPlan.id,
              // Zero means "nothing left to collect" — the server drops the
              // uncollectable rows and closes the plan.
              remainingPrincipal: 0,
            })
          }
          closingRemaining={rebuildPlan.isPending}
          focusInstallmentId={focusInstallmentId}
          onRecord={(inst) => {
            setUnadjustedMode(false);
            setFocusInstallmentId(inst.id);
            // Fill in what THIS dated instalment still owes. Clicking "Record"
            // on the 1 Sept row should not then make you retype ₹16,666.66 —
            // and retyping is where the paise get dropped.
            // Rounded to real paise — a raw Float subtraction here put
            // 6666.639999999999 in the box and then failed the pending cap.
            setAmount(String(outstandingOf(inst)));
            // The form's open state is owned by Ongoing (one card open at a
            // time), so ask rather than set — and only when it is not already
            // open, since onToggleAdd would otherwise close it.
            if (!isAddOpen) onToggleAdd();
          }}
        />
      )}

      {/* Inline payment form */}
      {isAddOpen && (
        <div ref={formRef} className="px-4 pb-4 border-t border-[#e6e8e7]">
          <div className="pt-3.5 flex flex-col gap-2.5">
            {/* Nothing can be collected on a fully-paid deal, so the form must
                say so instead of letting someone type an amount that is refused
                on submit. The schedule's own "close the remaining instalments"
                banner above is the actual next step. */}
            {deal.pendingAmount <= 0 && (
              <div className="flex items-start gap-1.5 rounded-lg bg-[#f0faf5] border border-[#c1e1d5] px-2.5 py-2">
                <Check className="size-3.5 text-[#156548] mt-[1px] shrink-0" />
                <p className="text-[10px] text-[#1a6b4c] leading-[1.45]">
                  <span className="font-semibold">
                    This deal is fully paid — nothing more can be collected.
                  </span>{" "}
                  Any amount entered here will be refused. If the schedule still
                  lists instalments, close them using the button above; that
                  finishes the plan.
                </p>
              </div>
            )}
            {/* On a deal with a plan the money must say which instalment it is
                for — full width, above the amount, because it drives what the
                amount field means. */}
            {/* Unadjusted money that covers the whole remaining schedule is
                almost never what someone means to record — they are settling the
                plan. Offer that, rather than parking cash as credit and leaving
                six instalments still reminding everyone. */}
            {unadjustedMode &&
              deal.installmentPlan &&
              parsedAmount > 0 &&
              unpaidInstallments.length > 0 &&
              parsedAmount >=
                unpaidInstallments.reduce((a, i) => a + i.outstanding, 0) && (
                <div className="flex items-start justify-between gap-2 rounded-lg bg-[#f0faf5] border border-[#c1e1d5] px-2.5 py-1.5">
                  <p className="text-[10px] text-[#1a6b4c] leading-[1.45]">
                    {fmt(parsedAmount)} covers everything still owed on this plan.
                    Foreclose instead — it settles every instalment and closes the
                    schedule, so the future dates stop reminding you.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowForeclose(true)}
                    className="shrink-0 rounded-lg bg-[#218760] px-2 py-1 text-[10px] font-medium text-white hover:bg-[#1a6b4c]"
                  >
                    Foreclose
                  </button>
                </div>
              )}

            {unadjustedMode && deal.installmentPlan && (
              <div className="flex items-center justify-between gap-2 rounded-lg bg-[#fff7ed] border border-[#fdba74] px-2.5 py-1.5">
                <p className="text-[10px] text-[#9a3412] leading-[1.45]">
                  Recording as <span className="font-semibold">unadjusted</span>{" "}
                  — not against any instalment. You can apply it to instalments
                  afterwards.
                </p>
                <button
                  type="button"
                  onClick={() => setUnadjustedMode(false)}
                  className="shrink-0 text-[10px] font-medium text-[#9a3412] underline"
                >
                  Pick an instalment
                </button>
              </div>
            )}
            {requiresInstallment && (
              <InstallmentPicker
                installments={unpaidInstallments}
                selectedId={focusInstallmentId}
                onSelect={(inst) => {
                  setFocusInstallmentId(inst.id);
                  // Prefill what is actually outstanding on that row — the
                  // number the operator is about to be handed.
                  if (!amount || Number(amount) === 0) {
                    setAmount(String(inst.outstanding));
                  }
                }}
                allocation={allocation}
                amount={parsedAmount}
                onForeclose={() => setShowForeclose(true)}
              />
            )}
            {/* Discount: the counsellor chooses EITHER a flat amount (the
                write-off, unchanged) OR a coupon code. */}
            {isDiscount && canApplyCoupon && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium text-[#697571] uppercase tracking-[0.04em]">
                  Discount by
                </span>
                <div className="inline-flex rounded-[8px] border border-[#e0c6ff] bg-[#faf8ff] p-[2px]">
                  {(["amount", "coupon"] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setDiscountMode(m)}
                      className={`h-6 px-2.5 text-[11px] rounded-[6px] font-medium transition-colors ${
                        discountMode === m
                          ? "bg-[#7c3aed] text-white"
                          : "text-[#7c3aed] hover:bg-[#f3eeff]"
                      }`}
                    >
                      {m === "amount" ? "Amount" : "Coupon code"}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {isCouponMode ? (
              <>
                <div className="grid grid-cols-3 gap-2.5">
                  <PaymentMethodDropdown value={method} onChange={setMethod} />
                </div>
                <DealCouponPanel
                  childId={Number(deal.dealId)}
                  appliedCoupon={deal.coupon ?? null}
                  onDone={() => {
                    onToggleAdd();
                    resetForm();
                  }}
                  onCancel={() => {
                    onToggleAdd();
                    resetForm();
                  }}
                />
              </>
            ) : (
            <>
            <div className="grid grid-cols-3 gap-2.5">
              <div className="relative">
                <span
                  className={`absolute left-3 top-1/2 -translate-y-1/2 text-[12px] font-medium ${isDiscount ? "text-[#8b5cf6]" : "text-[#9ba3a0]"}`}
                >
                  ₹
                </span>
                <input
                  type="number"
                  placeholder={
                    selectedInstallment
                      ? `#${selectedInstallment.seq} · ${fmt(selectedInstallment.outstanding)} left`
                      : `Max ${fmt(deal.pendingAmount)}`
                  }
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  max={deal.pendingAmount}
                  min={1}
                  step="any"
                  className={`h-9 w-full pl-7 pr-3 text-[13px] rounded-[8px] border bg-white tabular-nums font-medium ${
                    amount && !isAmountValid
                      ? "border-red-300 focus:ring-red-100"
                      : isDiscount
                        ? "border-[#8b5cf6]/30 focus:border-[#8b5cf6] focus:ring-[#8b5cf6]/10"
                        : "border-[#e2e4e3] focus:border-[#46a580] focus:ring-[#46a580]/10"
                  } focus:outline-none focus:ring-2 transition-all`}
                />
              </div>
              <PaymentMethodDropdown value={method} onChange={setMethod} />
              {!isDiscount && (
                <BankNameInput
                  value={bankName}
                  onChange={setBankName}
                  usedBankNames={usedBankNames}
                  placeholder="Bank name"
                  className="[&_input]:h-9 [&_input]:text-[13px] [&_input]:rounded-[8px]"
                />
              )}
              {isDiscount && (
                <input
                  type="text"
                  placeholder="Reason for discount"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="h-9 px-3 text-[13px] rounded-[8px] border border-[#8b5cf6]/30 bg-[#faf8ff] text-[#374741] placeholder:text-[#a78bfa] focus:border-[#8b5cf6] focus:outline-none focus:ring-2 focus:ring-[#8b5cf6]/10 transition-all"
                />
              )}
            </div>
            {!isDiscount && (
              <div className="grid grid-cols-2 gap-2.5">
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-medium text-[#697571] uppercase tracking-[0.04em]">
                    Received on
                  </label>
                  {/* Xale's own calendar + hour/minute/meridiem, not
                      `<input type="datetime-local">`. The native control renders
                      in the BROWSER's locale — so it shows a 24-hour clock, or
                      a US m/d/y order, depending on whose machine it is — and it
                      looks nothing like the rest of the app. These are the same
                      controls the leads filter drawer uses. */}
                  <SingleDatePicker
                    date={paidAtDate}
                    onDateChange={(d) => setPaidAt(joinDateTime(d, paidAtTime))}
                  />
                  <TimeOfDayField
                    label="Time"
                    value={paidAtTime}
                    fallback="09:00"
                    onChange={(t) => setPaidAt(joinDateTime(paidAtDate, t))}
                  />
                  <span className="text-[9px] text-[#9ba3a0]">
                    Defaults to now — change it if the money arrived earlier.
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-medium text-[#697571] uppercase tracking-[0.04em]">
                    Note
                  </label>
                  <input
                    type="text"
                    placeholder="Optional"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="h-9 px-3 text-[13px] rounded-[8px] border border-[#e2e4e3] bg-white focus:border-[#46a580] focus:outline-none focus:ring-2 focus:ring-[#46a580]/10 transition-all"
                  />
                </div>
              </div>
            )}
            <div className="flex items-center justify-between pt-0.5">
              <div className="flex items-center gap-2.5">
                {!isDiscount && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleReceiptUpload}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingReceipt}
                      className="flex items-center gap-1.5 h-8 px-3 text-[12px] font-medium rounded-[8px] border border-dashed border-[#c5c9c7] text-[#697571] hover:bg-[#f7f9f8] hover:border-[#9ba3a0] transition-all disabled:opacity-50"
                    >
                      {uploadingReceipt ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : (
                        <Upload className="size-3.5" />
                      )}
                      {receiptFileName || "Attach Receipt"}
                    </button>
                    {receiptDocId && (
                      <button
                        onClick={() => {
                          setReceiptDocId(null);
                          setReceiptFileName("");
                        }}
                        className="size-5 flex items-center justify-center rounded-full hover:bg-[#fee2e2] text-[#9ba3a0] hover:text-[#991b1b] transition-colors"
                      >
                        <X className="size-3" />
                      </button>
                    )}
                  </>
                )}
                {isDiscount && (
                  <span className="text-[11px] text-[#8b5cf6] font-medium flex items-center gap-1.5">
                    <Tag className="size-3" />
                    This will reduce the pending amount by ₹
                    {fmt(parsedAmount || 0)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {blockedReason && (
                  <span className="text-[10px] text-[#da2a46] leading-[1.4] max-w-[260px] text-right">
                    {blockedReason}
                  </span>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onToggleAdd();
                    resetForm();
                  }}
                  className="h-8 text-[12px] px-4 rounded-[8px] font-medium"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!canSave}
                  onClick={handleSave}
                  className={`h-8 text-[12px] px-4 rounded-[8px] font-medium ${isDiscount ? "!bg-[#7c3aed] hover:!bg-[#6d28d9]" : ""}`}
                >
                  {createPayment.isPending || settleInstallments.isPending ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : isDiscount ? (
                    "Apply Discount"
                  ) : (
                    "Save Payment"
                  )}
                </Button>
              </div>
            </div>
            </>
            )}
          </div>
        </div>
      )}

      {/* Razorpay "Collect payment": amount → link → copy / WhatsApp / email. */}
      {isRzpAddOpen && rzpConnected && paymentLinkLead?.id && (
        <CollectPaymentDialog
          open
          onOpenChange={setIsRzpAddOpen}
          initialDealId={Number(deal.dealId)}
          lead={{ ...paymentLinkLead, id: paymentLinkLead.id }}
        />
      )}

      {/* Razorpay payment links history (shown when there are any) */}
      <PaymentLinksList childId={Number(deal.dealId)} lead={paymentLinkLead} />

      {/* No-variant hint */}
      {hasNoVariant && !isAddOpen && (
        <div className="mx-4 mb-3 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-[#faf8ff] border border-dashed border-[#e0c6ff]">
          <LinkIcon className="size-3.5 text-[#a78bfa] shrink-0" />
          <p className="text-[12px] text-[#7c3aed]">
            {industry?.id === 1 ? "No course linked" : "No service variant linked"}
            {canAttachVariant
              ? " — attach one to enable payments"
              : ` — ${DEAL_EDIT_DENIED_MESSAGE.toLowerCase()}`}
          </p>
        </div>
      )}

      {/* Expanded history + financial breakdown */}
      {isHistoryOpen && (
        <div className="px-4 pb-4 border-t border-[#e6e8e7]">
          {/* Financial breakdown */}
          <div className="pt-3 pb-3 grid grid-cols-3 gap-x-6 gap-y-2">
            <FinRow label="Deal Value" value={fmt(deal.totalValue)} />
            <FinRow
              label="Addon"
              value={deal.addonAmount > 0 ? `+${fmt(deal.addonAmount)}` : "0"}
              color={deal.addonAmount > 0 ? "#0369a1" : "#9ba3a0"}
            />
            <FinRow
              label={deal.coupon ? `Discount (${deal.coupon.code})` : "Discount"}
              value={
                deal.discountAmount > 0 ? `-${fmt(deal.discountAmount)}` : "0"
              }
              color={deal.discountAmount > 0 ? "#EF5350" : "#9ba3a0"}
            />
            <FinRow label="Net Amount" value={fmt(deal.netAmount)} bold />
            <FinRow
              label="Advance"
              value={fmt(deal.advanceAmount)}
              color="#4a7cc9"
            />
            <FinRow
              label="Received"
              value={fmt(deal.totalPaid)}
              color="#2d7a5a"
            />
            <FinRow
              label="Pending"
              value={fmt(deal.pendingAmount)}
              color="#c77800"
              bold
            />
          </div>

          {/* Timeline */}
          <div className="pt-2.5 border-t border-[#f0f1f0]">
            <p className="text-[11px] font-semibold text-[#8a918d] uppercase tracking-[0.5px] mb-3">
              Payment History
            </p>
            <PaymentHistoryTimeline
              payments={deal.payments}
              dealId={deal.dealId}
              usedBankNames={usedBankNames}
            />
          </div>
        </div>
      )}

      {industry?.id === 1 ? (
        <AttachCourseModal
          open={showAttachModal}
          onClose={() => {
            // useAttachVariant refreshes the deal/lead caches on success.
            setShowAttachModal(false);
          }}
          dealId={Number(deal.dealId)}
          dealName={deal.dealName}
        />
      ) : (
        <AttachServiceModal
          open={showAttachModal}
          onClose={() => {
            // useAttachVariant refreshes the deal/lead caches on success.
            setShowAttachModal(false);
          }}
          dealId={Number(deal.dealId)}
          dealName={deal.dealName}
        />
      )}

      {/* Plan setup / rebuild. Sibling of the attach modals — it owns its own
          form state, so it stays mounted-by-flag rather than being re-created
          on every card render. */}
      {deal.installmentPlan && (
        <ForecloseDialog
          open={showForeclose}
          onClose={() => setShowForeclose(false)}
          childId={Number(deal.dealId)}
          dealName={deal.dealName}
          installments={deal.installmentPlan.installments}
          unadjusted={Math.max(
            0,
            deal.totalPaid -
              deal.installmentPlan.installments.reduce(
                (s, i) => s + i.paidAmount,
                0,
              ),
          )}
          dealPending={deal.pendingAmount}
          onRebuild={() => setShowPlanModal(true)}
        />
      )}

      <ConfirmDialog
        open={confirmRemoveCoupon}
        onClose={() => setConfirmRemoveCoupon(false)}
        onConfirm={() =>
          removeCoupon.mutate(Number(deal.dealId), {
            onSettled: () => setConfirmRemoveCoupon(false),
          })
        }
        title={`Remove coupon ${deal.coupon?.code ?? ""}?`}
        tone="warning"
        warningTitle="The deal's price goes back to what it was before the coupon"
        warningDescription="Any flat discount the coupon replaced comes back. The code's use is given back, so it can be applied again while it still has uses left."
        confirmLabel="Remove coupon"
        loadingLabel="Removing"
        isLoading={removeCoupon.isPending}
      />

      <InstallmentPlanModal
        open={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        childId={Number(deal.dealId)}
        dealName={deal.dealName}
        pendingAmount={deal.pendingAmount}
        plan={deal.installmentPlan}
      />
    </div>
  );
}

function FinRow({
  label,
  value,
  color,
  bold,
}: {
  label: string;
  value: string;
  color?: string;
  bold?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[12px] text-[#8a918d]">{label}</span>
      <span
        className={`text-[13px] tabular-nums ${bold ? "font-bold" : "font-semibold"}`}
        style={{ color: color || "#1e302a" }}
      >
        {value}
      </span>
    </div>
  );
}
